import { Link } from "react-router-dom";

export interface CaseCardData {
  slug: string;
  title: string;
  category: string;
  excerpt: string;
  cover: string;
  date: string;
  readTime: string;
  author: string;
}

interface CaseCardProps {
  article: CaseCardData;
  variant?: "default" | "wide" | "compact";
}

export default function CaseCard({ article, variant = "default" }: CaseCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/cases/${article.slug}`}
        className="group flex gap-4 p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-400 transition-colors cursor-pointer"
      >
        <div className="w-24 h-20 md:w-28 md:h-24 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title} · 网暴黑料调查`}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="flex-1 min-w-0">
          <span className="inline-block text-[11px] font-label px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800">
            {article.category}
          </span>
          <h3 className="mt-2 font-heading text-sm md:text-base font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="mt-1.5 text-xs text-foreground-500">
            {article.date} · {article.readTime}
          </p>
        </div>
      </Link>
    );
  }

  const wide = variant === "wide";

  return (
    <article
      className={`group h-full flex flex-col rounded-lg overflow-hidden border border-background-200 bg-background-50 hover:border-primary-400 transition-colors ${
        wide ? "" : "cursor-pointer"
      }`}
    >
      <Link to={`/cases/${article.slug}`} className="block cursor-pointer">
        <div className={`w-full ${wide ? "h-56 md:h-80" : "h-48 md:h-52"} bg-background-200`}>
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title} · 网暴黑料调查`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      </Link>
      <div className="flex-1 flex flex-col p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/cases?category=${encodeURIComponent(article.category)}`}
            className="text-[11px] font-label px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors cursor-pointer"
          >
            {article.category}
          </Link>
          <span className="text-[11px] text-foreground-500">{article.readTime}</span>
        </div>
        <h3
          className={`mt-3 font-heading font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors ${
            wide ? "text-lg md:text-2xl" : "text-base md:text-lg"
          }`}
        >
          <Link to={`/cases/${article.slug}`} className="cursor-pointer">
            {article.title}
          </Link>
        </h3>
        <p
          className={`mt-2.5 text-sm leading-relaxed text-foreground-600 ${
            wide ? "line-clamp-3" : "line-clamp-2"
          }`}
        >
          {article.excerpt}
        </p>
        <div className="mt-auto pt-4 flex items-center justify-between gap-3 text-xs text-foreground-500">
          <span className="truncate">{article.author}</span>
          <span className="whitespace-nowrap">{article.date}</span>
        </div>
      </div>
    </article>
  );
}